import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  dateToYmd,
  formatDisplayDate,
  parseYmdLocal,
  sanitizeStoredDateRange
} from '../utils/dateYmd';

const DateContext = createContext();

export const useDateContext = () => {
  const context = useContext(DateContext);
  if (!context) {
    throw new Error('useDateContext must be used within a DateProvider');
  }
  return context;
};

export const DateProvider = ({ children }) => {
  // Calculate default dates (tomorrow and day after tomorrow)
  const getDefaultDates = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    
    const dayAfterTomorrow = new Date();
    dayAfterTomorrow.setDate(dayAfterTomorrow.getDate() + 2);
    dayAfterTomorrow.setHours(0, 0, 0, 0);
    
    return {
      checkIn: formatDisplayDate(tomorrow),
      checkOut: formatDisplayDate(dayAfterTomorrow),
      checkInDate: tomorrow,
      checkOutDate: dayAfterTomorrow
    };
  };

  // Initialize from localStorage or use defaults
  const initializeDates = () => {
    try {
      const stored = localStorage.getItem('globalSelectedDates');
      if (stored) {
        const parsed = JSON.parse(stored);
        let checkInDate = null;
        let checkOutDate = null;

        if (parsed.checkInYmd && parsed.checkOutYmd) {
          checkInDate = parseYmdLocal(parsed.checkInYmd);
          checkOutDate = parseYmdLocal(parsed.checkOutYmd);
        } else if (parsed.checkInDate && parsed.checkOutDate) {
          checkInDate = new Date(parsed.checkInDate);
          checkOutDate = new Date(parsed.checkOutDate);
        }

        const sanitized = sanitizeStoredDateRange(checkInDate, checkOutDate);
        if (sanitized) {
          return {
            checkIn: formatDisplayDate(sanitized.checkInDate),
            checkOut: formatDisplayDate(sanitized.checkOutDate),
            checkInDate: sanitized.checkInDate,
            checkOutDate: sanitized.checkOutDate
          };
        }
        localStorage.removeItem('globalSelectedDates');
      }
    } catch (error) {
      console.warn('Failed to load dates from localStorage:', error);
    }
    return getDefaultDates();
  };

  const [selectedDates, setSelectedDates] = useState(initializeDates);

  // Save to localStorage whenever dates change
  useEffect(() => {
    try {
      localStorage.setItem(
        'globalSelectedDates',
        JSON.stringify({
          checkIn: selectedDates.checkIn,
          checkOut: selectedDates.checkOut,
          checkInYmd: dateToYmd(selectedDates.checkInDate),
          checkOutYmd: dateToYmd(selectedDates.checkOutDate),
          checkInDate: selectedDates.checkInDate?.toISOString(),
          checkOutDate: selectedDates.checkOutDate?.toISOString()
        })
      );
    } catch (error) {
      console.warn('Failed to save dates to localStorage:', error);
    }
  }, [selectedDates]);

  const updateDates = (dates) => {
    setSelectedDates(prev => ({
      ...prev,
      ...dates
    }));
  };

  const formatDateForDisplay = (date) => {
    if (!date) return '';
    if (date instanceof Date) return formatDisplayDate(date);
    return date;
  };

  const value = {
    selectedDates,
    updateDates,
    formatDateForDisplay,
    getDefaultDates
  };

  return (
    <DateContext.Provider value={value}>
      {children}
    </DateContext.Provider>
  );
};





